import {
  AnalysisStatusType,
  ExpenseSchema,
  ExpenseStatusType,
  ExtractDataFromExpense,
  type PaymentMethod,
  ZodCreateExpenseSchema,
  ZodEditExpenseSchema,
  ZodGetExpensesParams,
} from "@billingnow/zod-schema";
import { PaginationMeta, PaginationParams } from "./pagination";
import { apiClient } from "./client";

export type Expense = ExpenseSchema;
export type ExpenseAnalysisStatus = {
  id: string;
  analysisStatus: AnalysisStatusType | null;
  analysisError: string | null;
  extractedData: ExtractDataFromExpense | null;
  attachmentUrl: string | null;
  attachmentName: string | null;
};
export type ExpenseImageType = "logo" | "signature";

export const getExpense = async (expenseId: string): Promise<Expense> => {
  const response = await apiClient.get<{ expense: Expense }>(`/api/expense/${expenseId}`);
  return response.data.expense;
};

/**
 * Get expenses - returns all expenses (for backward compatibility)
 * @deprecated Use getExpensesPaginated for better performance with large datasets
 */
export const getExpenses = async (params?: ZodGetExpensesParams): Promise<Expense[]> => {
  const response = await apiClient.get<{ expenses: Expense[]; pagination: PaginationMeta }>("/api/expense", { params });
  return response.data.expenses;
};

/**
 * Paginated response for expenses
 */
export interface GetExpensesPaginatedResponse {
  expenses: Expense[];
  pagination: PaginationMeta;
}

/**
 * Get expenses with pagination support
 */
export const getExpensesPaginated = async (
  params?: ZodGetExpensesParams & PaginationParams,
): Promise<GetExpensesPaginatedResponse> => {
  const response = await apiClient.get<GetExpensesPaginatedResponse>("/api/expense", { params });
  return response.data;
};

export const createExpense = async (
  expenseData: ZodCreateExpenseSchema,
): Promise<{ success: boolean; message: string; expenseId: string }> => {
  const response = await apiClient.post<{ success: boolean; message: string; expenseId: string }>("/api/expense", expenseData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

export const updateExpense = async (
  expenseData: ZodEditExpenseSchema,
): Promise<{ success: boolean; message: string; expense: Expense }> => {
  const response = await apiClient.put<{ success: boolean; message: string; expense: Expense }>(
    `/api/expense/${expenseData.id}`,
    expenseData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const updateExpenseStatus = async (
  id: string,
  status: ExpenseStatusType,
): Promise<{ success: boolean; message: string; expense: Expense }> => {
  const response = await apiClient.patch<{ success: boolean; message: string; expense: Expense }>(
    `/api/expense/${id}/status`,
    { status },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const deleteExpense = async (id: string): Promise<{ message: string; expense: Expense }> => {
  const response = await apiClient.delete<{ message: string; expense: Expense }>(`/api/expense/${id}`);
  return response.data;
};

export const generateExpenseNumber = async (relationId: string): Promise<{ expenseNumber: string }> => {
  const response = await apiClient.post<{ expenseNumber: string }>(
    "/api/expense/generate-number",
    { relationId },
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
};

/**
 * Upload and analyze expense asynchronously (creates pending expense and triggers workflow)
 */
export const uploadAndAnalyzeExpense = async (
  attachmentUrl: string,
  attachmentName: string,
): Promise<{ expenseId: string; status: string }> => {
  // Step 1: Create pending expense in the API
  const createResponse = await apiClient.post<{ expenseId: string; status: string }>("/api/expense/upload-analyze", {
    attachmentUrl,
    attachmentName,
  });

  // Step 2: Trigger the workflow
  await apiClient.post("/api/workflow/expenses/analyze", {
    expenseId: createResponse.data.expenseId,
    userId: "current", // Will be resolved by the workflow
  });

  return createResponse.data;
};

/**
 * Get expense analysis status
 */
export const getExpenseAnalysisStatus = async (expenseId: string): Promise<ExpenseAnalysisStatus> => {
  const response = await apiClient.get<ExpenseAnalysisStatus>(`/api/expense/${expenseId}/analysis-status`);
  return response.data;
};

/**
 * Confirm expense data for confirmation
 */
export interface ConfirmExpenseData {
  relationId: string;
  relationName: string;
  relationAddressLine1?: string;
  relationAddressLine2?: string;
  relationCity?: string;
  relationState?: string;
  relationZipcode?: string;
  relationCountry?: string;
  relationBankAccountIban?: string;
  expenseNumber?: string;
  currency: string;
  exchangeRates?: Record<string, number>;
  date?: string;
  dueDate?: string;
  subtotal?: number;
  taxAmount?: number;
  discount?: number;
  total?: number;
  categoryId?: string | null;
  note?: string;
  isPaid?: boolean;
  paymentDate?: string;
  paymentMethod?: PaymentMethod | null;
  paymentReference?: string;
}

/**
 * Confirm expense analysis and apply extracted/edited data
 */
export const confirmExpenseAnalysis = async (
  expenseId: string,
  data?: ConfirmExpenseData,
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post<{ success: boolean; message: string }>(`/api/expense/${expenseId}/confirm`, data, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

/**
 * Retry failed expense analysis
 */
export const retryExpenseAnalysis = async (expenseId: string): Promise<{ expenseId: string; status: string }> => {
  // Step 1: Reset expense status to pending
  const response = await apiClient.post<{ expenseId: string; status: string }>(`/api/expense/${expenseId}/retry`);

  // Step 2: Trigger the workflow again
  await apiClient.post("/api/workflow/expenses/analyze", {
    expenseId: response.data.expenseId,
    userId: "current",
  });

  return response.data;
};

/**
 * Get all expenses (fetches all pages for export)
 */
export const getAllExpenses = async (params?: ZodGetExpensesParams): Promise<Expense[]> => {
  const allBills: Expense[] = [];
  let cursor: string | null = null;

  do {
    const response = await getExpensesPaginated({ ...params, cursor, limit: 100 });
    allBills.push(...response.expenses);
    cursor = response.pagination.nextCursor;
  } while (cursor);

  return allBills;
};
